/*
 * <hp-scroll-rail> — mouse controls for a native horizontal scroller.
 *
 * Touch keeps native swiping; nothing here changes it. For a mouse the rail adds:
 * - click-and-drag to scroll; a flick carries on to the nearest tile, and the click
 *   that ends a drag is swallowed so it never selects a pot or hits an Add button;
 * - prev/next arrows (injected here; CSS shows them only on hover-capable fine pointers
 *   and only when there is more to see that way);
 * - data-overflow / data-can-prev / data-can-next / data-dragging for the CSS.
 *
 * Markup: <hp-scroll-rail class="hp-rail"><div data-hp-rail-track>…tiles…</div></hp-scroll-rail>
 * The track keeps its own overflow-x and scroll-snap styles; its element children are the
 * tiles. Used by snippets/pot-selector.liquid and sections/hp-side-cart.liquid. The side
 * cart swaps regions by innerHTML and fills pot trays later, so everything is wired in
 * connectedCallback, torn down in disconnectedCallback, and tile changes are observed.
 */
if (!customElements.get('hp-scroll-rail')) {
  const DRAG_THRESHOLD = 6; // px a press must travel before it becomes a drag
  const EDGE = 8; // px from either end that still counts as "at the end"
  const FLICK_WINDOW = 100; // ms of pointer samples used for the release velocity
  const FLICK_CARRY = 180; // ms the release velocity keeps travelling for
  const SETTLE_IDLE = 120; // ms without a scroll event that means the settle finished

  const CHEVRON = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M9 5l7 7-7 7"/></svg>';

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class HpScrollRail extends HTMLElement {
    constructor() {
      super();
      this.drag = null;
      this.frame = 0;
      this.settleTimer = 0;
      this.settling = false;
      this.suppressClick = false;

      this.update = this.update.bind(this);
      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onDragStart = (event) => event.preventDefault();
      this.restoreSnap = this.restoreSnap.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-hp-rail-track]');
      if (!this.track) return;

      if (!this.prevButton || !this.contains(this.prevButton)) {
        this.prevButton = this.buildButton('prev');
        this.nextButton = this.buildButton('next');
        this.append(this.prevButton, this.nextButton);
      }

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('dragstart', this.onDragStart);
      this.addEventListener('click', this.onClickCapture, true);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
        this.resizeObserver.observe(this.track);
      }
      this.mutationObserver = new MutationObserver(this.scheduleUpdate);
      this.mutationObserver.observe(this.track, { childList: true });

      this.scheduleUpdate();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.endDrag();
      this.restoreSnap();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('dragstart', this.onDragStart);
      this.removeEventListener('click', this.onClickCapture, true);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.mutationObserver.disconnect();
    }

    /* Mouse-only affordance: keyboard users reach every tile by tabbing, and focus
     * scrolls it into view, so the arrows stay out of the tab order and the a11y tree. */
    buildButton(direction) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `hp-rail__btn hp-rail__btn--${direction}`;
      button.tabIndex = -1;
      button.setAttribute('aria-hidden', 'true');
      button.innerHTML = CHEVRON;
      // Keep focus where it was: the arrow hides itself at either end of the row.
      button.addEventListener('mousedown', (event) => event.preventDefault());
      button.addEventListener('click', () => this.step(direction === 'next' ? 1 : -1));
      return button;
    }

    /* ---------------------------------------------------------------- *
     * State for the CSS
     * ---------------------------------------------------------------- */

    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }

    update() {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      const left = track.scrollLeft;
      const overflow = max > EDGE;
      this.toggleAttribute('data-overflow', overflow);
      this.toggleAttribute('data-can-prev', overflow && left > EDGE);
      this.toggleAttribute('data-can-next', overflow && left < max - EDGE);
    }

    onScroll() {
      this.scheduleUpdate();
      if (this.settling) {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(this.restoreSnap, SETTLE_IDLE);
      }
    }

    /* ---------------------------------------------------------------- *
     * Geometry
     * ---------------------------------------------------------------- */

    /* Tiles as {start, end} in scroll coordinates, plus the snap padding */
    measure() {
      const track = this.track;
      const style = getComputedStyle(track);
      const origin = track.getBoundingClientRect().left + track.clientLeft - track.scrollLeft;
      const tiles = [];
      Array.prototype.forEach.call(track.children, (child) => {
        const rect = child.getBoundingClientRect();
        if (rect.width) tiles.push({ start: rect.left - origin, end: rect.right - origin });
      });
      return {
        tiles,
        padStart: parseFloat(style.scrollPaddingLeft) || 0,
        padEnd: parseFloat(style.scrollPaddingRight) || 0,
        max: track.scrollWidth - track.clientWidth,
      };
    }

    nearestSnap(position) {
      const { tiles, padStart, max } = this.measure();
      let best = clamp(position, 0, max);
      let bestDistance = Infinity;
      tiles.forEach((tile) => {
        const snap = clamp(tile.start - padStart, 0, max);
        const distance = Math.abs(snap - position);
        if (distance < bestDistance) {
          best = snap;
          bestDistance = distance;
        }
      });
      return best;
    }

    /* Page by whole tiles: next brings the first clipped tile to the start, prev
     * brings the last tile clipped on the left to the end. */
    step(direction) {
      const track = this.track;
      const { tiles, padStart, padEnd, max } = this.measure();
      const left = track.scrollLeft;
      const view = track.clientWidth;
      let target;

      if (direction > 0) {
        const tile = tiles.find((t) => t.end > left + view - padEnd + 1);
        target = tile ? tile.start - padStart : max;
        if (target <= left + 1) target = left + view * 0.8; // a tile wider than the view
      } else {
        const visibleStart = left + padStart;
        const clipped = tiles.filter((t) => t.start < visibleStart - 1).pop();
        if (!clipped) {
          target = 0;
        } else {
          const want = clipped.end + padEnd - view;
          const tile = tiles.find((t) => t.start - padStart >= want - 1);
          target = tile ? tile.start - padStart : 0;
        }
        if (target >= left - 1) target = left - view * 0.8;
      }

      this.restoreSnap();
      track.scrollTo({ left: clamp(target, 0, max), behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    /* ---------------------------------------------------------------- *
     * Drag
     * ---------------------------------------------------------------- */

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!this.hasAttribute('data-overflow')) return;

      this.endDrag();
      this.drag = {
        id: event.pointerId,
        startX: event.clientX,
        startLeft: 0,
        active: false,
        samples: [],
      };
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.startX;

      if (!drag.active) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        drag.active = true;
        // Measured from the threshold, not the press, so the tiles never jump by it,
        // and from the current scrollLeft so a grab mid-glide picks up where it had got to.
        drag.startX += Math.sign(dx) * DRAG_THRESHOLD;
        drag.startLeft = this.track.scrollLeft;
        this.setAttribute('data-dragging', '');
        // Snap would fight every scrollLeft write; it comes back once the drag settles.
        this.cancelSettle();
        this.track.style.scrollSnapType = 'none';
        this.track.style.scrollBehavior = 'auto';
        try {
          this.track.setPointerCapture(drag.id);
        } catch (error) {
          /* pointer already released */
        }
        const selection = window.getSelection && window.getSelection();
        if (selection && selection.removeAllRanges) selection.removeAllRanges();
      }

      this.track.scrollLeft = drag.startLeft - (event.clientX - drag.startX);
      drag.samples.push({ t: event.timeStamp, x: event.clientX });
      while (drag.samples.length > 2 && event.timeStamp - drag.samples[0].t > FLICK_WINDOW) drag.samples.shift();
    }

    onPointerUp(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      this.endDrag();
      if (!drag.active) return;

      // The click that follows this pointerup belongs to the drag, not to a tile.
      this.suppressClick = true;
      setTimeout(() => {
        this.suppressClick = false;
      }, 0);

      let velocity = 0; // px of scroll per ms
      const first = drag.samples[0];
      const last = drag.samples[drag.samples.length - 1];
      if (event.type === 'pointerup' && first && last.t > first.t && event.timeStamp - last.t < FLICK_WINDOW) {
        velocity = -(last.x - first.x) / (last.t - first.t);
      }
      // A hard flick glides at most one view further, never straight to the far end.
      const view = this.track.clientWidth;
      const carry = clamp(velocity * FLICK_CARRY, -view, view);
      this.settle(this.nearestSnap(this.track.scrollLeft + carry));
    }

    endDrag() {
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
      this.removeAttribute('data-dragging');
      this.drag = null;
    }

    onClickCapture(event) {
      if (!this.suppressClick) return;
      this.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }

    /* Glide to a snap point with snapping still off, then hand snapping back. */
    settle(target) {
      const track = this.track;
      if (Math.abs(target - track.scrollLeft) < 1) {
        this.restoreSnap();
        return;
      }
      this.settling = true;
      track.scrollTo({ left: target, behavior: reducedMotion() ? 'auto' : 'smooth' });
      clearTimeout(this.settleTimer);
      // Settled = no scroll event for SETTLE_IDLE (see onScroll). Not scrollend: one queued
      // by the drag's own writes can land mid-glide and switch snapping back on too early.
      this.settleTimer = setTimeout(this.restoreSnap, SETTLE_IDLE + 30);
    }

    cancelSettle() {
      clearTimeout(this.settleTimer);
      this.settling = false;
    }

    restoreSnap() {
      this.cancelSettle();
      if (!this.track) return;
      this.track.style.scrollSnapType = '';
      this.track.style.scrollBehavior = '';
    }
  }

  customElements.define('hp-scroll-rail', HpScrollRail);
}
